import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLines, Reveal, SectionHeading, LeafSprig } from "@/components/Motion";
import { Marquee } from "@/components/Marquee";
import { IMAGES, TEAM, TIMELINE, VALUES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const StoryHero = () => (
  <section className="relative overflow-hidden bg-obsidian pb-24 pt-36 sm:pb-32" data-testid="story-hero">
    <LeafSprig className="pointer-events-none absolute -left-8 bottom-4 h-72 -rotate-12 text-leaf-light/12" />
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <motion.img
          src="/images/logo-duo.png"
          alt="Root2Shoot — Sustainable Café and Film & TV Catering"
          className="mb-9 h-14 w-auto sm:h-16"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.8 }}
          data-testid="story-logo"
        />
        <motion.p
          className="label-mono flex items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
        >
          Our Story &amp; Team
          <span className="h-px w-20 bg-gold/50" aria-hidden="true" />
        </motion.p>
        <MaskedLines
          lines={["From small seed"]}
          className="mt-6 font-editorial text-5xl leading-[1.0] tracking-tight text-cream sm:text-6xl lg:text-7xl"
        />
        <MaskedLines
          as="p"
          lines={["to on-screen impact"]}
          className="font-editorial text-4xl leading-[1.02] tracking-tight text-gold sm:text-5xl lg:text-6xl"
          delay={0.4}
        />
        <motion.p
          className="mt-8 max-w-lg text-base leading-relaxed text-sage sm:text-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
        >
          Root2Shoot grew from one belief: food can be a force for good — for the people
          who grow it, the people who cook it, and the people it brings together.
        </motion.p>
      </div>
      <motion.div
        className="relative lg:col-span-5"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.45, duration: 1.1, ease: EASE }}
      >
        <div className="absolute -inset-0 translate-x-4 translate-y-4 border border-gold/40" aria-hidden="true" />
        <img
          src={IMAGES.fruitPlatter}
          alt="A generous fresh fruit platter prepared by the Root2Shoot kitchen"
          className="relative aspect-[4/5] w-full object-cover"
          data-testid="story-hero-image"
        />
      </motion.div>
    </div>
  </section>
);

const Statement = () => (
  <section className="bg-paper py-24 text-ink sm:py-32" data-testid="mission-statement-section">
    <div className="mx-auto max-w-5xl px-5 sm:px-8">
      <Reveal>
        <div className="relative border-y border-gold-deep/40 py-16 sm:py-20">
          <span className="absolute -top-8 left-0 select-none font-display text-[110px] italic leading-none text-gold-deep/40" aria-hidden="true">&ldquo;</span>
          <p className="label-mono-ink flex items-center gap-4">
            Our Mission Statement
            <span className="h-px w-20 bg-gold-deep/60" aria-hidden="true" />
          </p>
          <blockquote className="mt-8 font-display text-2xl font-medium italic leading-snug text-ink sm:text-3xl lg:text-[2.6rem]" data-testid="mission-statement">
            Root2Shoot is more than a catering company. It's a community with a kitchen
            at its heart.
          </blockquote>
          <div className="mt-10 grid gap-8 text-base leading-relaxed text-ink/70 sm:grid-cols-2">
            <p>
              We're a collective of chefs, growers, foragers, music lovers, nature enthusiasts
              and lifelong learners, brought together by a shared belief that great food can
              make a positive difference.
            </p>
            <p>
              We don't claim to have all the answers. We're still learning, still growing and
              still finding better ways to tread a little lighter on the planet every day.
              Whether it's reducing waste, supporting local producers, growing our own
              ingredients or discovering more sustainable ways to cook, we're always asking
              ourselves, "Can we do this better?"
            </p>
          </div>
          <p className="mt-10 max-w-2xl text-base leading-relaxed text-ink/70">
            If we can serve incredible food, bring people together, make someone smile and
            leave the world just a little better than we found it, we'd call that a pretty
            good day's work.
          </p>
          <p className="mt-10 font-display text-xl italic text-gold-deep sm:text-2xl" data-testid="mission-closing-line">
            We genuinely give a damn… and we think you can taste the difference.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const TeamGrid = () => (
  <section className="bg-obsidian py-24 sm:py-32" data-testid="team-grid-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading
        kicker="Meet the Team"
        title={<>Our people, <span className="text-gold">our craft</span></>}
      />
      <Reveal delay={0.1}>
        <p className="mt-7 max-w-2xl text-base leading-relaxed text-sage sm:text-lg">
          Food has always been our common language, but we each bring something different to
          the table.
        </p>
      </Reveal>
      <div className="mt-20 space-y-28">
        {TEAM.map((member, i) => (
          <Reveal key={member.name} delay={0.05}>
            <article className="grid items-center gap-12 lg:grid-cols-12" data-testid={`team-card-${member.name.toLowerCase()}`}>
              <div className={`relative lg:col-span-4 ${i % 2 === 1 ? "lg:order-2 lg:col-start-9" : ""}`}>
                <div className={`absolute -inset-0 border border-gold/40 ${i % 2 === 1 ? "-translate-x-3 translate-y-3" : "translate-x-3 translate-y-3"}`} aria-hidden="true" />
                <img
                  src={member.photo}
                  alt={`${member.name} of the Root2Shoot team — Root2Shoot sustainable café and film & TV catering, Stockport`}
                  className="relative aspect-[4/5] w-full object-cover object-top"
                  loading="lazy"
                  decoding="async"
                  data-testid={`team-photo-${member.name.toLowerCase()}`}
                />
              </div>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1" : "lg:col-start-6"}`}>
                <h3 className="font-editorial text-5xl tracking-tight text-cream sm:text-6xl lg:text-7xl">{member.name}</h3>
                <p className="label-mono mt-5">{member.role}</p>
                <p className="mt-7 max-w-xl text-base leading-relaxed text-sage">{member.bio}</p>
                <div className="mt-8 flex flex-wrap gap-2">
                  {member.tags.map((tag) => (
                    <span key={tag} className="border border-leaf/50 px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-leaf-light">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <div className="mt-24 border-y border-gold/25 py-14" data-testid="team-food-philosophy">
          <p className="label-mono mb-7 flex items-center gap-4 !text-leaf-light">
            Every Angle of Food
            <span className="h-px w-20 bg-leaf/50" aria-hidden="true" />
          </p>
          <p className="max-w-4xl font-display text-xl italic leading-relaxed text-cream/90 sm:text-2xl lg:text-3xl">
            Between us, we understand food from every angle. Kevin's journey into vegetarian
            cooking came through necessity, Kaytie's through lifelong choice and Harvey proudly
            flies the flag for the carnivores. Whether it's allergies, medical requirements,
            religious beliefs or lifestyle choices, we know everybody's relationship with food
            is different — and we believe everyone deserves exceptional food without compromise.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const Timeline = () => (
  <section className="bg-paper py-24 text-ink sm:py-32" data-testid="timeline-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading
        tone="light"
        kicker="The Journey So Far"
        title={<>How we <span className="font-display italic text-gold-deep">took root</span></>}
        className="mb-20"
      />
      <div className="relative space-y-16 before:absolute before:bottom-0 before:left-[7px] before:top-2 before:w-px before:bg-gradient-to-b before:from-gold-deep/60 before:via-leaf/50 before:to-transparent md:before:left-1/2">
        {TIMELINE.map((step, i) => (
          <Reveal key={step.title} delay={0.05}>
            <div className="relative grid gap-4 pl-10 md:grid-cols-2 md:gap-16 md:pl-0" data-testid={`timeline-step-${i}`}>
              <span className="absolute left-0 top-2 h-3.5 w-3.5 rotate-45 border-2 border-gold-deep bg-paper md:left-1/2 md:-translate-x-1/2" />
              <div className={i % 2 === 0 ? "md:pr-16 md:text-right" : "md:order-2 md:pl-16"}>
                <p className="label-mono-ink">{step.year}</p>
                <h3 className="mt-2 font-serif text-2xl tracking-tight text-ink sm:text-3xl">{step.title}</h3>
              </div>
              <p className={`text-sm leading-relaxed text-ink/65 sm:text-base ${i % 2 === 0 ? "md:order-2 md:pl-16 md:text-left" : "md:pr-16 md:text-right"}`}>
                {step.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Pillars = () => (
  <section className="bg-obsidian py-24 sm:py-32" data-testid="pillars-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading
        kicker="Our Values"
        title={<>Three pillars, <span className="text-gold">one plate</span></>}
        className="mb-16"
      />
      <div className="grid gap-12 md:grid-cols-3">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.08}>
            <div className="border-t-2 border-gold/60 pt-7" data-testid={`pillar-${v.title.toLowerCase()}`}>
              <p className="font-display text-xl italic text-gold">0{i + 1}</p>
              <h3 className="mt-3 font-editorial text-3xl tracking-tight text-cream">{v.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-sage sm:text-base">{v.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-24">
        <div className="border-y border-gold/25 py-16 text-center" data-testid="story-cta-banner">
          <h2 className="font-editorial text-3xl tracking-tight text-cream sm:text-5xl">
            Want to <span className="font-display italic text-gold">talk?</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-sage">
            Café questions, catering quotes or just to say hello — it all reaches the same small team.{" "}
            <Link to="/sustainability" className="text-gold underline-offset-4 hover:underline" data-testid="story-sustainability-link">
              Read how we operate
            </Link>{" "}
            before you do.
          </p>
          <Link to="/contact" className="btn-gold mt-10" data-testid="story-cta-contact">
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Story() {
  return (
    <>
      <Seo
        title="Our Story & Team — Root2Shoot Sustainable Café & Catering"
        siteName="Root2Shoot"
        description="Meet Kevin, Kaytie and Harvey — the Stockport team behind Root2Shoot's sustainable café and plant-forward film & TV catering. From a weekend market stall to feeding productions."
        image="/images/logo-duo.png"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Our Story & Team", path: "/our-story" }]}
        jsonLd={[
          { "@context": "https://schema.org", "@type": "AboutPage", name: "Our Story & Team — Root2Shoot" },
          ...TEAM.map((m) => ({
            "@context": "https://schema.org",
            "@type": "Person",
            name: m.name,
            jobTitle: m.name === "Harvey" ? "Hospitality Student" : m.role,
            worksFor: { "@type": "Organization", name: "Root2Shoot" },
            image: `${window.location.origin}${m.photo}`,
          })),
        ]}
      />
      <StoryHero />
      <Marquee />
      <Statement />
      <TeamGrid />
      <Timeline />
      <Pillars />
    </>
  );
}
